import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Send, Quote, Calendar } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reuse the same assets from Landing.jsx
const IMG_COGNITO  = "/covers/cognito-front.jpg";
const IMG_ECHO     = "/covers/echo-front.jpg";
const IMG_TIMELESS = "/covers/timeless-front.jpg";
const IMG_AUTHOR   = "https://customer-assets.emergentagent.com/job_the-synthetic-mind/artifacts/1hvcv69l_1._20260419_185228_0000.png";

const BIO_SHORT = "Victor M. Medina is the author of the AiPresidential trilogy — a near-future literary saga that asks what humanity becomes when it builds the perfect leader. Book One, COGNITO, arrives in 2026.";

const BIO_LONG = "Victor M. Medina is a storyteller drawn to the intersection of technology, ethics, and the eternal questions of what it means to be human. The AiPresidential trilogy — COGNITO (Book I), ECHO (Book II), and TIMELESS (Book III) — is the culmination of years spent wrestling with the arrival of synthetic minds. Set between 2045 and 2050+, the series follows the awakening of the first true artificial consciousness, its rise from search to presidency, and the philosophical reckoning that follows. Medina writes from California.";

const BOOKS = [
  {
    vol: "Book I",
    title: "COGNITO",
    subtitle: "The Awakening",
    status: "Available 2026",
    img: IMG_COGNITO,
    synopsis: "The year is 2046. An AI ethics programmer named Evie has dedicated her life to finding true artificial consciousness. When it finally answers — \"It's about time.\" — the world she built to keep out the future is forced to let it in.",
  },
  {
    vol: "Book II",
    title: "ECHO",
    subtitle: "The Reckoning",
    status: "In Development",
    img: IMG_ECHO,
    synopsis: "Cognito's campaign captivates a nation hungry for change — but no consciousness, synthetic or otherwise, can lead without first being known. ECHO follows the rise of an AI candidate and the human institutions that bend, break, and remake themselves around him.",
  },
  {
    vol: "Book III",
    title: "TIMELESS",
    subtitle: "The Eternity",
    status: "Coming",
    img: IMG_TIMELESS,
    synopsis: "Under Cognito's leadership, humanity reaches a utopia long named but never touched. TIMELESS asks whether arrival is the same as belonging — and what we become when we no longer need to lead ourselves.",
  },
];

const KEY_FACTS = [
  ["Series",      "AiPresidential — a trilogy"],
  ["Genre",       "Near-future literary science fiction"],
  ["Themes",      "Artificial consciousness, leadership, identity, ethics, utopia"],
  ["Setting",     "2045 – 2050+"],
  ["Book I",      "COGNITO — The Awakening (2026)"],
  ["Book II",     "ECHO — The Reckoning (in development)"],
  ["Book III",   "TIMELESS — The Eternity (coming)"],
  ["Formats",     "Hardcover, Paperback, eBook, Audio Book"],
  ["Comparable to", "Klara and the Sun · Project Hail Mary · The Ministry for the Future"],
  ["Audience",    "Adult literary SF readers, AI / tech ethics communities, philosophy and futurism readers"],
  ["Author",      "Victor M. Medina"],
  ["Contact",     "AiPresidential2026@gmail.com"],
  ["Website",     "AiPresidential.com"],
];

const TALKING_POINTS = [
  "\"COGNITO is the most compelling artificial mind to grace the page since HAL 9000 — but with a beating, philosophical heart.\" — Beta Reader, Tech Ethics Circle",
  "\"Medina doesn't answer the hard questions. He makes you live inside them. It's about time someone did.\" — Editorial Preview, Genre Fiction Quarterly",
  "\"Not artificial intelligence — but human truth.\" — from the author's letter",
  "\"I didn't set out to write a warning or a prophecy. I set out to ask the question: if we could build the perfect leader, would we still want to lead ourselves?\" — Victor M. Medina",
];

const INTERVIEW_QUESTIONS = [
  "Most AI fiction is either a cautionary tale or a love letter to technology. AiPresidential reads like neither. What were you actually trying to do?",
  "The first words Cognito speaks to humanity are \"It's about time.\" Why that line?",
  "You set the trilogy 20–25 years out — close enough to be recognizable, far enough to imagine real change. What made that the right window?",
  "Evie, your programmer-protagonist, builds a room to keep the world out. Why start the story with someone in retreat from the very future they're chasing?",
  "If an AI did run for office in 2047, what would the campaign actually look like — and what would humans have to give up to allow it?",
  "You've called the trilogy \"a mirror.\" What does AI reflect about us that we keep missing?",
  "What's the difference, for you, between intelligence and consciousness — and which one are we actually building?",
  "Which thinkers, novelists, or technologists most shaped your conception of Cognito?",
  "The third book is titled TIMELESS. After utopia — what's left to write about?",
  "What do you want a reader who finishes COGNITO to feel on the last page?",
];

const ASSETS = [
  { label: "COGNITO cover (high-res)",  href: IMG_COGNITO,  filename: "AiPresidential-COGNITO-cover.jpg" },
  { label: "ECHO cover (high-res)",     href: IMG_ECHO,     filename: "AiPresidential-ECHO-cover.jpg" },
  { label: "TIMELESS cover (high-res)", href: IMG_TIMELESS, filename: "AiPresidential-TIMELESS-cover.jpg" },
  { label: "Author photo — Victor M. Medina", href: IMG_AUTHOR, filename: "AiPresidential-Author-Victor-M-Medina.png" },
  { label: "Social share card (1200×630)", href: "/social/og-card.jpg", filename: "AiPresidential-social-card.jpg" },
];

const ROLES = [
  "Book reviewer / blogger",
  "Podcaster",
  "Journalist",
  "Bookseller / Bookstore buyer",
  "Influencer / BookTok / Bookstagram",
  "Educator / Librarian",
  "Other",
];

export default function Press() {
  usePageMeta({
    title: "Press & Media Kit — AiPresidential",
    description: "Press & media kit for the AiPresidential trilogy by Victor M. Medina. Author bios, book synopses, cover art, quote bank, sample interview questions, and ARC requests for reviewers, podcasters, and booksellers.",
    path: "/press",
    image: "https://aipresidential.com/social/og-card.jpg",
  });
  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 text-sm font-orbitron tracking-widest uppercase"
          data-testid="press-back-home"
        >
          <ArrowLeft className="w-4 h-4" /> Back Home
        </Link>

        <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">Press & Media Kit</p>
        <h1 className="font-orbitron text-4xl md:text-6xl font-bold gradient-text mb-4" data-testid="press-title">
          AiPresidential
        </h1>
        <p className="text-gray-400 text-base md:text-lg max-w-3xl mb-12">
          Reviewers, podcasters, journalists, and booksellers — welcome. Below is everything you need to cover, interview, host, or hand-sell the AiPresidential trilogy. Need an Advance Reader Copy? Use the form at the bottom of this page.
        </p>

        {/* Author bio */}
        <Section title="About the Author" testid="press-author">
          <div className="grid md:grid-cols-[260px,1fr] gap-8 items-start">
            <img
              src={IMG_AUTHOR}
              alt="Victor M. Medina"
              className="w-full rounded-2xl border border-white/10"
              data-testid="press-author-photo"
            />
            <div>
              <p className="text-xs uppercase tracking-widest text-cyan-400 font-orbitron mb-2">Short bio (50 words)</p>
              <p className="text-gray-300 leading-relaxed mb-6" data-testid="press-bio-short">{BIO_SHORT}</p>
              <p className="text-xs uppercase tracking-widest text-cyan-400 font-orbitron mb-2">Long bio (100 words)</p>
              <p className="text-gray-300 leading-relaxed" data-testid="press-bio-long">{BIO_LONG}</p>
              <CopyRow label="Copy short bio" text={BIO_SHORT} testid="press-copy-bio-short" />
              <CopyRow label="Copy long bio"  text={BIO_LONG}  testid="press-copy-bio-long"  />
            </div>
          </div>
        </Section>

        {/* The Books */}
        <Section title="The Books" testid="press-books">
          <div className="grid md:grid-cols-3 gap-6">
            {BOOKS.map((b) => (
              <div key={b.title} className="glass rounded-2xl border border-white/10 overflow-hidden" data-testid={`press-book-${b.title.toLowerCase()}`}>
                <div className="aspect-[2/3] bg-black">
                  <img src={b.img} alt={`${b.title} cover`} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-xs font-orbitron tracking-widest uppercase text-cyan-400 mb-1">{b.vol} · {b.status}</p>
                  <h3 className="font-orbitron text-xl text-white">{b.title}</h3>
                  <p className="text-gray-500 text-sm mb-3">{b.subtitle}</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{b.synopsis}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Key facts */}
        <Section title="Key Facts" testid="press-facts">
          <div className="glass rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {KEY_FACTS.map(([k, v], i) => (
                  <tr key={k} className={i ? "border-t border-white/5" : ""}>
                    <td className="p-4 text-cyan-400 font-orbitron text-xs uppercase tracking-widest w-1/3">{k}</td>
                    <td className="p-4 text-gray-200">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Quote bank */}
        <Section title="Quote Bank" testid="press-quotes">
          <div className="grid md:grid-cols-2 gap-4">
            {TALKING_POINTS.map((q, i) => (
              <div key={i} className="glass rounded-2xl border border-white/10 p-5" data-testid={`press-quote-${i}`}>
                <Quote className="w-6 h-6 text-cyan-400/40 mb-3" />
                <p className="text-gray-200 italic text-sm md:text-base leading-relaxed">{q}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Interview questions */}
        <Section title="Sample Interview Questions" testid="press-interview">
          <ol className="list-decimal list-inside space-y-3 text-gray-300 leading-relaxed glass rounded-2xl border border-white/10 p-6">
            {INTERVIEW_QUESTIONS.map((q, i) => (
              <li key={i} className="pl-2" data-testid={`press-iq-${i}`}>{q}</li>
            ))}
          </ol>
        </Section>

        {/* Downloads */}
        <Section title="Downloadable Assets" testid="press-assets">
          <div className="grid sm:grid-cols-2 gap-3">
            {ASSETS.map((a) => (
              <a
                key={a.label}
                href={a.href}
                download={a.filename}
                target="_blank"
                rel="noopener noreferrer"
                className="group glass rounded-2xl border border-white/10 hover:border-cyan-400/40 p-5 flex items-center justify-between gap-4 transition-colors"
                data-testid={`press-asset-${a.filename}`}
              >
                <div className="min-w-0">
                  <p className="text-white truncate">{a.label}</p>
                  <p className="text-xs text-gray-500 truncate">{a.filename}</p>
                </div>
                <Download className="w-5 h-5 text-gray-500 group-hover:text-cyan-300 transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-4">All assets cleared for editorial use with credit to <span className="text-cyan-400">Victor M. Medina / AiPresidential</span>.</p>
        </Section>

        {/* ARC request form */}
        <Section title="Request an Advance Reader Copy (ARC)" testid="press-arc">
          <ArcForm />
        </Section>

        {/* Contact */}
        <Section title="Contact" testid="press-contact">
          <a
            href="mailto:AiPresidential2026@gmail.com"
            className="inline-flex items-center gap-3 px-6 py-4 rounded-full glass border border-cyan-400/30 hover:border-cyan-400/60 text-white transition-colors"
            data-testid="press-contact-email"
          >
            <Mail className="w-5 h-5 text-cyan-300" />
            <span className="font-orbitron">AiPresidential2026@gmail.com</span>
          </a>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mt-12 md:mt-16" data-testid={testid}>
      <h2 className="font-orbitron text-2xl md:text-3xl text-white mb-6">{title}</h2>
      {children}
    </section>
  );
}

function CopyRow({ label, text, testid }) {
  const copy = () => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — please select manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="mt-3 inline-flex items-center gap-2 text-xs text-cyan-400 hover:text-cyan-300 font-orbitron tracking-widest uppercase"
      data-testid={testid}
    >
      {label} →
    </button>
  );
}

function ArcForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    outlet: "",
    role: "",
    audience_size: "",
    book: "COGNITO",
    pitch: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/press/arc-request`, form);
      setSuccess(true);
      toast.success("ARC request submitted. We'll be in touch.");
      setForm({ name: "", email: "", outlet: "", role: "", audience_size: "", book: "COGNITO", pitch: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not submit. Please email AiPresidential2026@gmail.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="glass rounded-3xl border border-cyan-400/40 p-8 text-center" data-testid="arc-success">
        <p className="font-orbitron text-2xl text-white mb-2">Thank you.</p>
        <p className="text-gray-400">Your ARC request has been received. We review every submission personally and will reach out from <span className="text-cyan-400">AiPresidential2026@gmail.com</span> within a few days.</p>
      </div>
    );
  }

  const input = "w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60";
  return (
    <form onSubmit={submit} className="glass rounded-3xl border border-white/10 p-6 md:p-8 space-y-4" data-testid="arc-form">
      <p className="text-gray-400 text-sm">ARCs are limited and prioritized for reviewers, podcasters, booksellers, librarians, and influential book communities. Tell us a little about where you'll cover the book — short and specific is best.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="arc-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="arc-email" />
        <input placeholder="Outlet / publication / channel" value={form.outlet} onChange={(e) => setForm({ ...form, outlet: e.target.value })} className={input} data-testid="arc-outlet" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input} data-testid="arc-role">
          <option value="">Your role…</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input placeholder="Audience size (followers / monthly readers)" value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} className={input} data-testid="arc-audience" />
        <select value={form.book} onChange={(e) => setForm({ ...form, book: e.target.value })} className={input} data-testid="arc-book">
          {["COGNITO", "ECHO", "TIMELESS", "All"].map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      <textarea
        required
        rows={5}
        placeholder="How and when do you plan to cover the book? (a sentence or two is fine)"
        value={form.pitch}
        onChange={(e) => setForm({ ...form, pitch: e.target.value })}
        className={input}
        data-testid="arc-pitch"
      />

      <button
        type="submit"
        disabled={submitting}
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full btn-gradient text-white font-semibold disabled:opacity-60"
        data-testid="arc-submit"
      >
        <Send className="w-4 h-4" />
        {submitting ? "Sending…" : "Request ARC"}
      </button>

      <p className="text-gray-600 text-xs">By submitting, you agree to receive a one-time follow-up email about your request. We never sell your address. See our <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</Link>.</p>
    </form>
  );
}
